import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import {
	ClotureTraitementWithDate,
	ClotureTraitementWithDateError,
	ClotureTraitementWithDateSuccess,
	DeleteTraitement,
	DeleteTraitementError,
	DeleteTraitementSuccess,
	GenericErrorTraitement,
	ReprendreListTraitementBetweenDates,
	ReprendreListTraitementBetweenDatesError,
	ReprendreListTraitementBetweenDatesSuccess,
	SuspendreListTraitementBetweenDates,
	SuspendreListTraitementBetweenDatesError,
	SuspendreListTraitementBetweenDatesSuccess,
	TraitementActionTypes,
} from "@featuresAY/ordonnance-web-ay/store/traitement/traitement.action";
import { concatMap, from, Observable, of, switchMap, toArray } from "rxjs";
import { TraitementApiService } from "@featuresAY/ordonnance-web-ay/services/api/traitement-api.service";
import { catchError, tap } from "rxjs/operators";

@Injectable()
export class TraitementV2Effects {
	private traitementApiService = inject(TraitementApiService);

	constructor(private actions$: Actions) {}

	clotureTraitementWithDate = createEffect(() =>
		this.actions$.pipe(
			ofType<ClotureTraitementWithDate>(TraitementActionTypes.ClotureTraitementWithDate),
			switchMap((action) => {
				return this.traitementApiService
					.clotureTraitementByDate(action.payload.id, action.payload.clotureInfo)
					.pipe(
						switchMap((traitement) => {
							return [new ClotureTraitementWithDateSuccess(traitement)];
						}),
						catchError((error) => of(new ClotureTraitementWithDateError(error))),
					);
			}),
			catchError((error) => of(new GenericErrorTraitement(error))),
		),
	);

	suspendreTraitementBetweenDates = createEffect(() =>
		this.actions$.pipe(
			ofType<SuspendreListTraitementBetweenDates>(TraitementActionTypes.SuspendreListTraitementBetweenDates),
			switchMap((action) => {
				const obs: Observable<number>[] = action.payload.traitementListIds.map((traitementId) =>
					this.traitementApiService.suspendreTraitementBetweenDates(
						traitementId,
						action.payload.dateDebut,
						action.payload.dateFin,
					),
				);
				return from(obs).pipe(
					concatMap((o) => o),
					toArray(),
					switchMap((_) => {
						return [new SuspendreListTraitementBetweenDatesSuccess()];
					}),
					catchError((error) => of(new SuspendreListTraitementBetweenDatesError(error))),
				);
			}),
		),
	);

	reprendreListTraitementBetweenDates = createEffect(() =>
		this.actions$.pipe(
			ofType<ReprendreListTraitementBetweenDates>(TraitementActionTypes.ReprendreListTraitementBetweenDates),
			switchMap((action) => {
				const obs: Observable<number>[] = action.payload.traitementListIds.map((traitementId) =>
					this.traitementApiService.reprendreListTraitementBetweenDates(
						traitementId,
						action.payload.dateDebut,
						action.payload.dateFin,
					),
				);
				return from(obs).pipe(
					concatMap((o) => o),
					toArray(),
					switchMap((_) => {
						return [new ReprendreListTraitementBetweenDatesSuccess()];
					}),
					catchError((error) => of(new ReprendreListTraitementBetweenDatesError(error))),
				);
			}),
		),
	);

	deleteTraitement$ = createEffect(() =>
		this.actions$.pipe(
			ofType<DeleteTraitement>(TraitementActionTypes.DeleteTraitement),
			switchMap((action) => {
				return this.traitementApiService.deleteTraitement(action.payload.id).pipe(
					switchMap(() => {
						return [new DeleteTraitementSuccess()];
					}),
					catchError((error) => of(new DeleteTraitementError(error))),
				);
			}),
			catchError((error) => of(new GenericErrorTraitement(error))),
		),
	);

	genericError$ = createEffect(() =>
		this.actions$.pipe(
			ofType<GenericErrorTraitement>(TraitementActionTypes.GenericErrorTraitement),
			tap((action) => {
				console.error(action.payload);
			}),
		),
	);
}
